import h from 'hyperscript';

import type { DisplayMode } from 'ui/datasource-tile/datasource-tile';
import { createDataSourceTile } from 'ui/datasource-tile/datasource-tile';
import type { ResourceType } from 'ui/resource-label/resource-label';
import { createResourceLabelHtml } from 'ui/resource-label/resource-label';
import { Select } from 'ui/select/select';
import type { SelectOption, SelectOptionGroup } from 'ui/select/select-types';

import type { DataSourceFlavor } from 'cadenza/repository/data-source-flavor';
import type { DataSourceType } from 'cadenza/repository/model/data-source-type.gen';
import { showResourceDropdownSearch, toResourceSelectOption } from 'cadenza/resource-filter/resource-filter';
import { markSubstring } from 'cadenza/utils/mark-substring';

import './datasource-filter.css';
import i18n from './datasource-filter.properties';

export interface DatasourceSelectOption extends SelectOption {
  resourceType: ResourceType;
  type: DataSourceType;
  flavor?: DataSourceFlavor;
  repositoryPrintName?: string;
  textOnly?: boolean;
  showIcon?: boolean;
}

export interface DatasourceSelectOptionGroup extends SelectOptionGroup {
  resourceType: ResourceType;
  children: DatasourceSelectOption[];
  type: DataSourceType;
  flavor?: DataSourceFlavor;
  repositoryPrintName?: string;
  textOnly?: boolean;
  showIcon?: boolean;
}

export type DatasourceSelectItem = DatasourceSelectOption | DatasourceSelectOptionGroup;

export function createDatasourceFilter(
  data: DatasourceSelectItem[],
  filterKey: string,
  { prependSelectAll = true }: { prependSelectAll?: boolean } = {},
): Select {
  let extendedData = data;
  if (prependSelectAll) {
    extendedData = prependSelectAllOption(data);
  }
  const filter = new Select({
    data: extendedData,
    styleClass: 'filter-control',
    templateResult: (item, searchText) => templateDatasourceResult(item as DatasourceSelectItem, searchText),
    templateGroupResult: (item) => templateGroupDatasourceResult(item as DatasourceSelectItem),
    templateSelection: (item) => templateDatasourceSelection(item as DatasourceSelectItem, true),
    searchEnabled: showResourceDropdownSearch(data),
  });
  filter.setAttribute('name', filterKey);
  return filter;
}

function prependSelectAllOption(values: DatasourceSelectItem[]): DatasourceSelectItem[] {
  return [
    {
      id: '',
      text: i18n('filter.datasource.all'),
      resourceType: 'datasource',
      flavor: 'H2',
      type: 'RDBMS',
      addSeparationLineBelow: true,
      textOnly: true,
      showIcon: false,
    },
    ...values,
  ];
}

function itemDisplayMode(item: DatasourceSelectItem, singleLine: boolean) {
  if (item.textOnly) {
    return item.showIcon ? 'textWithIcon' : 'onlyText';
  } else {
    return singleLine ? 'singleLine' : 'complete';
  }
}

export function templateDatasourceResult(item: DatasourceSelectItem, searchText: string) {
  const displayMode: DisplayMode = itemDisplayMode(item, false);
  const text = searchText ? markSubstring(item.text, searchText) : item.text;
  return h<HTMLElement>(
    '.datasource-result.ellipsis',
    createDataSourceTile(text, item.type, item.flavor, {
      displayMode,
    }),
  );
}

export function templateGroupDatasourceResult(item: DatasourceSelectItem) {
  return h<HTMLElement>('.repository-result.ellipsis', {
    innerHTML: createResourceLabelHtml('repository', item.text),
  });
}

function templateDatasourceSelection(item: DatasourceSelectItem, singleLine = false) {
  const displayMode: DisplayMode = itemDisplayMode(item, singleLine);
  return createDataSourceTile(item.text, item.type, item.flavor, { displayMode, styleClass: 'datasource-selection' });
}

export function toDataSourceSelectOption(
  item: SelectOption,
  type: DataSourceType,
  flavor?: DataSourceFlavor,
  repositoryPrintName?: string,
): DatasourceSelectOption {
  return { ...toResourceSelectOption(item, 'datasource'), type, flavor, repositoryPrintName };
}

export function toDatasourceSelectOptionGroup(
  text: string,
  children: DatasourceSelectOption[],
  type: DataSourceType,
  flavor?: DataSourceFlavor,
  repositoryPrintName?: string,
): DatasourceSelectOptionGroup {
  return { text, children, resourceType: 'datasource', type, flavor, repositoryPrintName };
}
